import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import shippingService from "../../services/shippingService";

/**
 * Fetch available shipping methods based on order amount
 */
export const fetchAvailableShippingMethods = createAsyncThunk(
  "shipping/fetchAvailable",
  async (orderAmount, { rejectWithValue }) => {
    try {
      return await shippingService.getAvailableShippingMethods(orderAmount);
    } catch (error) {
      return rejectWithValue(
        error.message || "Failed to fetch shipping methods"
      );
    }
  }
);

/**
 * Initial state for shipping slice
 */
const initialState = {
  availableShippingMethods: [],
  selectedMethod: null,
  status: "idle", // 'idle' | 'loading' | 'succeeded' | 'failed'
  error: null,
};

/**
 * Shipping slice for Redux store
 */
const shippingSlice = createSlice({
  name: "shipping",
  initialState,
  reducers: {
    /**
     * Set the selected shipping method
     * @param {object} state - Current state
     * @param {object} action - Action with shipping method as payload
     */
    selectShippingMethod: (state, action) => {
      state.selectedMethod = action.payload;
    },

    /**
     * Reset shipping state
     * @param {object} state - Current state
     */
    resetShipping: (state) => {
      state.selectedMethod = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchAvailableShippingMethods.pending, (state) => {
        state.status = "loading";
      })
      .addCase(fetchAvailableShippingMethods.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.availableShippingMethods = action.payload;

        // If there are shipping methods and none is selected,
        // automatically select the first one
        if (action.payload.length > 0) {
          // If we have a previously selected method, try to keep the same one
          if (state.selectedMethod) {
            // Find the same method in the new list (by ID)
            const existingMethod = action.payload.find(
              (m) => m.id === state.selectedMethod.id
            );

            if (existingMethod) {
              // Update to the refreshed version of the same method
              state.selectedMethod = existingMethod;
            } else {
              // If previously selected method is no longer available, select the first one
              state.selectedMethod = action.payload[0];
            }
          } else {
            // If no method was selected before, select the first one
            state.selectedMethod = action.payload[0];
          }
        } else {
          // No methods available, clear selection
          state.selectedMethod = null;
        }
      })
      .addCase(fetchAvailableShippingMethods.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload || "Failed to fetch shipping methods";
      });
  },
});

export const { selectShippingMethod, resetShipping } = shippingSlice.actions;
export default shippingSlice.reducer;
